import { useMemo, useState } from 'react';
import { AutocompleteOption, LwFormAutocomplete } from 'redesign';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDebounce } from 'shared/hooks/use-debounce';
import { useGetWorkerList } from 'hq/hooks/queryHooks/flexWorkers/use-get-flex-worker-list';
import { EmploymentType } from '@types';
import { WorkerList } from 'hq/services/flex-worker-service.types';
import { useTranslation } from 'react-i18next';
import { getFullNameWithInfix } from './utils';

export type FlexWorkerFormSelectProps = {
  disabled?: boolean;
  name: string;
  label?: string;
};

export const getWorkerLabel = (worker: WorkerList) => {
  return getFullNameWithInfix(worker) + ' (' + worker.email + ')';
};

export const FlexWorkerFormSelect = (props: FlexWorkerFormSelectProps) => {
  const { name, disabled, label = 'Flexwerker' } = props;
  const { t } = useTranslation(['hq-screen-specific']);
  const { control, setValue } = useFormContext();
  const selectedValue = useWatch({ control, name }) as AutocompleteOption | null;
  const [inputValue, setInputValue] = useState(selectedValue?.label || '');
  const debouncedFlexWorkerSearch = useDebounce(inputValue, 300);
  const flexWorkersQuery = useGetWorkerList(debouncedFlexWorkerSearch, [EmploymentType.TEMPWORKER]);

  const options = useMemo(
    () =>
      (flexWorkersQuery.data || []).map((worker) => ({
        label: getWorkerLabel(worker),
        value: worker.workerId,
      })),
    [flexWorkersQuery.data]
  );

  return (
    <LwFormAutocomplete<AutocompleteOption>
      name={name}
      control={control}
      label={label}
      disabled={disabled}
      isLoading={flexWorkersQuery.isLoading}
      rules={{
        required: {
          value: true,
          message: t('flex_worker_select_section.validation.required', {
            ns: 'hq-screen-specific',
          }),
        },
      }}
      options={options}
      noOptionsText={t('flex_worker_select_section.validation.required', {
        ns: 'hq-screen-specific',
      })}
      isOptionEqualToValue={(option, value) => option?.value === value?.value}
      getOptionKey={(option) => option.value}
      getOptionLabel={(option) => option.label}
      inputValue={inputValue}
      onInputChange={(_e, value) => {
        setInputValue(value);
      }}
      onChange={(_e, select) => {
        setValue(name, select, { shouldValidate: true });
      }}
    />
  );
};
